import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { type ColumnDef } from "@tanstack/react-table";
import { IconChartBar, IconCopy, IconExternalLink, IconSend, IconVideo, IconX } from "@tabler/icons-react";
import toast from "react-hot-toast";

import { UserAvatar } from "@/components/common/UserAvatar";
import { DashboardCard, StatusText } from "@/components/dashboard/dashboard-card";
import { DataTable } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { cn } from "@/lib/utils";
import {
  cancelTikTokAdRequest,
  listTikTokAdRequests,
  resendTikTokAdRequest,
  type TikTokAdRequest,
  type TikTokAdRequestStatus,
} from "@/services/tiktokShop.service";
import { formatAmount, formatCompactCount } from "@/utils/formatters";

import { AD_REQUEST_STATUS_LABEL, errorMessage, shortDate } from "../tiktokFormat";

type Filter = "OPEN" | "DONE" | "CLOSED";
const FILTERS: Array<{ value: Filter; label: string }> = [
  { value: "OPEN", label: "Waiting on creator" },
  { value: "DONE", label: "Submitted" },
  { value: "CLOSED", label: "Closed" },
];
const FILTER_STATUSES: Record<Filter, TikTokAdRequestStatus[]> = {
  OPEN: ["REQUESTED", "ACCEPTED"],
  DONE: ["SUBMITTED", "APPROVED", "LAUNCHED"],
  CLOSED: ["REJECTED", "CANCELLED", "EXPIRED"],
};

/** StatusText tones keyed off words the dashboard kit already knows. */
const STATUS_TONE: Record<TikTokAdRequestStatus, string> = {
  REQUESTED: "PENDING",
  ACCEPTED: "ACTIVE",
  SUBMITTED: "PENDING",
  APPROVED: "APPROVED",
  LAUNCHED: "ACTIVE",
  REJECTED: "REJECTED",
  CANCELLED: "INACTIVE",
  EXPIRED: "EXPIRED",
};

export function AdRequestStatus({ status }: { status: TikTokAdRequestStatus }) {
  return <StatusText status={STATUS_TONE[status]} label={AD_REQUEST_STATUS_LABEL[status]} />;
}

function payLabel(r: TikTokAdRequest): string {
  const parts = [r.flatFee ? `${formatAmount(r.flatFee)} fee` : null, r.commissionRate > 0 ? `${r.commissionRate}%` : null].filter(Boolean);
  return parts.length ? parts.join(" + ") : "No pay";
}

/**
 * Every "request for ads" the brand sent from a sample video: who it went
 * to, how it was delivered, and where the creator's file is in review.
 */
export function AdRequestsTab() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<Filter>("OPEN");

  const list = useQuery({ queryKey: ["tiktok-ad-requests"], queryFn: () => listTikTokAdRequests() });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["tiktok-ad-requests"] });

  const resend = useMutation({
    mutationFn: resendTikTokAdRequest,
    onSuccess: (r) => {
      toast.success(r.dmStatus === "SENT" ? "Message sent on TikTok" : "TikTok did not take the message");
      void refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const cancel = useMutation({
    mutationFn: cancelTikTokAdRequest,
    onSuccess: () => {
      toast.success("Request cancelled");
      void refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const rows = useMemo(() => (list.data ?? []).filter((r) => FILTER_STATUSES[filter].includes(r.status)), [list.data, filter]);
  const counts = useMemo(() => {
    const c: Record<Filter, number> = { OPEN: 0, DONE: 0, CLOSED: 0 };
    for (const r of list.data ?? []) {
      for (const f of FILTERS) if (FILTER_STATUSES[f.value].includes(r.status)) c[f.value] += 1;
    }
    return c;
  }, [list.data]);

  const copyLink = async (r: TikTokAdRequest) => {
    try {
      await navigator.clipboard.writeText(r.link);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy the link");
    }
  };

  const columns = useMemo<ColumnDef<TikTokAdRequest>[]>(
    () => [
      {
        id: "creator",
        header: "Creator",
        cell: ({ row }) => {
          const r = row.original;
          return (
            <span className="flex min-w-0 items-center gap-2.5">
              <UserAvatar src={r.creator.avatarUrl} name={r.creator.nickname || r.creator.username} className="size-7 shrink-0 text-[10px]" />
              <span className="min-w-0">
                <span className="block max-w-[14rem] truncate font-medium text-foreground" title={`@${r.creator.username}`}>
                  @{r.creator.username}
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  {r.affiliate ? (r.affiliate.enrolled ? r.affiliate.name : `${r.affiliate.name}, not enrolled yet`) : "Not on Reveshare yet"}
                </span>
              </span>
            </span>
          );
        },
      },
      {
        id: "video",
        header: "Video",
        cell: ({ row }) => {
          const v = row.original.video;
          const link = v.pageLink ?? v.url;
          return (
            <span className="flex min-w-0 items-center gap-2">
              <IconVideo size={16} stroke={1.75} className="shrink-0 text-muted-foreground" aria-hidden />
              <span className="min-w-0">
                <span className="block max-w-[16rem] truncate text-foreground" title={v.description ?? undefined}>
                  {v.description || "Untitled video"}
                </span>
                <span className="block text-xs tabular-nums text-muted-foreground">
                  {formatCompactCount(v.viewCount)} views · {v.paidOrderCount} paid orders
                  {link && (
                    <a href={link} target="_blank" rel="noreferrer" className="ml-1.5 inline-flex items-center gap-0.5 hover:text-foreground hover:underline">
                      Open <IconExternalLink size={12} stroke={1.75} aria-hidden />
                    </a>
                  )}
                </span>
              </span>
            </span>
          );
        },
      },
      {
        id: "program",
        header: "Program",
        cell: ({ row }) => {
          const t = row.original.target;
          const where = t ? `${t.campaignName ?? "campaign"}${t.adsetName ? ` › ${t.adsetName}` : ""}` : null;
          return (
            <span className="block min-w-0">
              <span className="block max-w-[12rem] truncate text-foreground" title={row.original.program.title}>
                {row.original.program.title}
              </span>
              <span className="block max-w-[12rem] truncate text-xs text-muted-foreground" title={where ?? undefined}>
                {where ? `In ${where}` : "New campaign"}
              </span>
            </span>
          );
        },
      },
      {
        id: "pay",
        header: "Pay",
        meta: { fit: true },
        cell: ({ row }) => <span className="whitespace-nowrap tabular-nums text-foreground">{payLabel(row.original)}</span>,
      },
      {
        id: "status",
        header: "Status",
        meta: { fit: true },
        cell: ({ row }) => {
          const r = row.original;
          return (
            <span className="block">
              <AdRequestStatus status={r.status} />
              <span className="block text-xs text-muted-foreground">
                {r.dmStatus === "FAILED"
                  ? "TikTok message failed"
                  : r.dmStatus === "SENT"
                    ? `Messaged ${shortDate(r.createdAt)}`
                    : r.status === "REQUESTED" || r.status === "ACCEPTED"
                      ? `Open until ${shortDate(r.expiresAt)}`
                      : shortDate(r.submittedAt ?? r.createdAt)}
              </span>
            </span>
          );
        },
      },
      {
        id: "actions",
        header: "",
        meta: { fit: true },
        cell: ({ row }) => {
          const r = row.original;
          const open = r.status === "REQUESTED" || r.status === "ACCEPTED";
          return (
            <span className="flex items-center justify-end gap-1">
              {r.draftAd?.metaAdId ? (
                <button
                  type="button"
                  title="Ad performance"
                  aria-label="Ad performance"
                  onClick={() => navigate(`/brand/meta-ads/performance?ad=${r.draftAd!.metaAdId}`)}
                  className="rounded-sm p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <IconChartBar size={16} stroke={1.75} />
                </button>
              ) : null}
              {r.content ? (
                <button
                  type="button"
                  onClick={() => navigate(`/brand/content-programs/${r.program.id}`)}
                  className="text-xs font-medium text-muted-foreground hover:text-foreground hover:underline"
                >
                  Review
                </button>
              ) : null}
              {open && (
                <>
                  <button type="button" title="Copy link" aria-label="Copy link" onClick={() => void copyLink(r)} className="rounded-sm p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    <IconCopy size={16} stroke={1.75} />
                  </button>
                  <button type="button" title="Send on TikTok again" aria-label="Send on TikTok again" disabled={resend.isPending} onClick={() => resend.mutate(r.id)} className="rounded-sm p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50">
                    <IconSend size={16} stroke={1.75} />
                  </button>
                  <button type="button" title="Cancel request" aria-label="Cancel request" disabled={cancel.isPending} onClick={() => cancel.mutate(r.id)} className="rounded-sm p-1 text-muted-foreground hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50">
                    <IconX size={16} stroke={1.75} />
                  </button>
                </>
              )}
            </span>
          );
        },
      },
    ],
    [navigate, resend, cancel],
  );

  return (
    <div className="space-y-4">
      <div role="tablist" aria-label="Request status" className="inline-flex rounded-lg border border-border bg-muted p-0.5">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            role="tab"
            aria-selected={filter === f.value}
            onClick={() => setFilter(f.value)}
            className={cn(
              "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              filter === f.value ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {f.label}
            <span className="ml-1.5 tabular-nums text-muted-foreground">{counts[f.value]}</span>
          </button>
        ))}
      </div>

      <DashboardCard title="Ad requests" bodyClassName="p-0">
        {list.error ? (
          <EmptyState bordered={false} title="Could not load requests" body={errorMessage(list.error)} className="py-10" />
        ) : !list.isLoading && rows.length === 0 ? (
          <EmptyState
            bordered={false}
            icon={<IconVideo size={20} stroke={1.75} />}
            title={filter === "OPEN" ? "No open requests" : "Nothing here"}
            body={filter === "OPEN" ? "Open a sample request and pick a video to ask for" : undefined}
            className="py-10"
          />
        ) : (
          <DataTable columns={columns} data={rows} isLoading={list.isLoading} />
        )}
      </DashboardCard>
    </div>
  );
}
